import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useState, useCallback, useRef } from 'react'
import { Check, Copy, Mail, Phone } from 'lucide-react'
import { contactCTAs, copyActions } from './config'
import type { ChatMessageProps } from '@/types/chatbot'

export function ChatMessage({ message, isTyping = false, isLast, ...props }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(async (value: string, id: string) => {
    try {
      await navigator.clipboard.writeText(value)

      // Clear existing timeout if any
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setCopiedId(id)
      timeoutRef.current = setTimeout(() => {
        setCopiedId(null)
        timeoutRef.current = null
      }, 3000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [])

  return (
    <div
      className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}
      {...props}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3',
          isUser
            ? 'bg-primary-500 text-white rounded-br-sm'
            : 'bg-white text-gray-900 rounded-bl-sm shadow-sm border border-gray-100'
        )}
      >
        {isTyping ? (
          <div
            className="flex gap-1.5 py-1 px-1"
            role="status"
            aria-label={t('chatbot.typing')}
          >
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        )}
      </div>

      {/* Copy Actions - only for last assistant message */}
      {!isUser && !isTyping && message.content && isLast && copyActions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2 ml-1">
          {copyActions.map((action) => {
            const isCopied = copiedId === action.id
            const Icon = action.type === 'email' ? Mail : Phone
            return (
              <button
                key={action.id}
                onClick={() => handleCopy(action.value, action.id)}
                className={cn(
                  'flex items-center gap-1.5 text-xs transition-all duration-200 font-medium cursor-pointer',
                  'px-3 py-2 rounded-full border min-h-[32px]',
                  isCopied
                    ? 'bg-green-50 text-green-600 border-green-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                )}
                aria-label={t(action.ariaLabelKey, { value: action.value })}
                title={t(action.tooltipKey, { value: action.value })}
              >
                {isCopied ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
                <span>{isCopied ? t('chatbot.copied') : t(action.labelKey)}</span>
                {!isCopied && <Copy className="w-3 h-3 ml-0.5 opacity-60" />}
              </button>
            )
          })}
        </div>
      )}

      {/* Contact CTAs - only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && isLast && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 ml-1">
          {contactCTAs.map((cta) => {
            // If CTA has copyValue, render as button that copies
            if (cta.copyValue) {
              const isCopied = copiedId === cta.id
              return (
                <button
                  key={cta.id}
                  onClick={() => handleCopy(cta.copyValue!, cta.id)}
                  className={cn(
                    'flex items-center gap-1.5 text-xs transition-all duration-200 font-medium cursor-pointer',
                    isCopied ? 'text-green-600' : 'text-primary-500 hover:text-primary-600'
                  )}
                >
                  {cta.icon && (
                    <img src={cta.icon} alt="" className="w-4 h-4" />
                  )}
                  <span>{isCopied ? t('chatbot.copied') : t(cta.labelKey)}</span>
                  {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
              )
            }

            // Otherwise render as link
            return (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-primary-500 hover:text-primary-600 transition-colors font-medium"
              >
                {cta.icon && (
                  <img src={cta.icon} alt="" className="w-4 h-4" />
                )}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
